import React, { useState, useEffect } from "react";
import Accordion from "../componentLibrary/Accordion";
import Button from "../componentLibrary/Button";
import Input from "../componentLibrary/Input";
import Modal from "../componentLibrary/Modal";
import * as client from "../api/client";
import { useUpdateDisplayName } from "../api/mutations";
import { useNotification } from "../hooks/useNotification";
import { usePermissions } from "../contexts/PermissionContext";

interface ChangeDisplayNameModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  title?: string;
  isInitialPrompt?: boolean; // When true, closing without saving will save username as displayName
}

const ChangeDisplayNameModal: React.FC<ChangeDisplayNameModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  title = "Change Display Name",
  isInitialPrompt = false,
}) => {
  const { notify } = useNotification();
  const { userProfile, refetchProfile } = usePermissions();

  const [displayName, setDisplayName] = useState("");
  const username = userProfile?.username || "";

  // Initialize with current display name (or username as fallback) when modal opens
  useEffect(() => {
    if (isOpen) {
      setDisplayName(userProfile?.displayName || userProfile?.username || "");
    }
  }, [isOpen, userProfile?.displayName, userProfile?.username]);

  const updateDisplayNameMutation = useUpdateDisplayName({
    onSuccess: async () => {
      await refetchProfile();
      notify("success", "Display name updated successfully!");
      handleCloseInternal();
      if (onSuccess) onSuccess();
    },
    onError: async () => {
      notify("error", "Failed to update display name.");
    },
  });

  const handleCloseInternal = () => {
    setDisplayName("");
    onClose();
  };

  const handleClose = () => {
    handleCloseInternal();
    // If dismissed without saving on the initial prompt, silently fall back to
    // username as the display name so this modal won't appear again.
    if (isInitialPrompt && !userProfile?.displayName) {
      client.updateDisplayName(username);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // If user submits with empty/whitespace, save the username instead
    const nameToSave = displayName.trim() || username;
    updateDisplayNameMutation.mutate(nameToSave);
  };

  const currentDisplayName = userProfile?.displayName || username;

  return (
    <Modal isOpen={isOpen} onCloseModal={handleClose} title={title}>
      <div className="space-y-6">
        <form onSubmit={handleSubmit}>
          <Input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Display Name"
            maxLength={50}
          />
          <p className="text-xs text-gray-500 mt-1">
            Leave empty to use your username ({username}) instead.
          </p>
          <p className="text-xs text-gray-500 mt-1">
            You can change this later in &quot;My Profile&quot;.
          </p>

          <Accordion
            className="mt-4"
            title={
              <span className="text-sm font-medium text-gray-800">
                What is a Display Name?
              </span>
            }
            headerClassName="px-3 py-2 text-left"
          >
            <div className="p-4 border-t border-gray-200 bg-white text-sm text-gray-600 space-y-2 rounded-b-lg">
              <p>
                Your display name is how the app greets you. Only you can see
                this name - it won&apos;t change your username or affect your
                account.
              </p>
              <p>
                Current display name:{" "}
                <strong className="font-semibold text-electrum">
                  {currentDisplayName}
                </strong>
              </p>
            </div>
          </Accordion>

          <div className="flex flex-col xl:flex-row justify-end gap-3 mt-6">
            <Button
              type="button"
              onClick={handleClose}
              variant="secondary"
              className="w-full xl:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={updateDisplayNameMutation.isPending}
              className="w-full xl:w-auto"
            >
              Save
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ChangeDisplayNameModal;
